import React, { useState } from 'react';
import { adminFileAPI } from '../services/api';
import ConfirmationModal from './ConfirmationModal';

// File Preview Component
const FilePreview = ({ file }) => {
  const getFileType = (fileType, fileName) => {
    if (!fileType) return 'unknown';

    const type = fileType.toLowerCase();
    const name = fileName?.toLowerCase() || '';

    if (type.startsWith('image/') || /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(name)) {
      return 'image';
    }
    if (type === 'application/pdf' || name.endsWith('.pdf')) {
      return 'pdf';
    }
    if (type.startsWith('text/') || /\.(txt|md|json|xml|html|css|js|ts)$/i.test(name)) {
      return 'text';
    }
    if (type.startsWith('video/') || /\.(mp4|avi|mov|wmv|flv|webm)$/i.test(name)) {
      return 'video';
    }
    if (type.startsWith('audio/') || /\.(mp3|wav|aac|ogg)$/i.test(name)) {
      return 'audio';
    }

    return 'download';
  };

  const fileType = getFileType(file.file_type, file.original_filename);

  switch (fileType) {
    case 'image':
      return (
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--bg-secondary)',
        }}>
          <img
            src={file.url}
            alt={file.title || file.original_filename}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              borderRadius: 'var(--border-radius)',
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div style={{
            display: 'none',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            color: 'var(--text-secondary)',
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>
              🖼️
            </div>
            <p>Image preview not available</p>
            <a
              href={file.url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
              style={{ marginTop: '1rem', padding: '0.5rem 1rem' }}
            >
              📎 Open Image
            </a>
          </div>
        </div>
      );

    case 'pdf':
      return (
        <div style={{
          width: '100%',
          height: '100%',
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: 'var(--border-radius)',
        }}>
          <iframe
            src={file.url}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              borderRadius: 'var(--border-radius)',
            }}
            title={file.title || file.original_filename}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div style={{
            display: 'none',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            textAlign: 'center',
            color: 'var(--text-secondary)',
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>
              📄
            </div>
            <p>PDF preview not available</p>
            <a
              href={file.url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
              style={{ marginTop: '1rem', padding: '0.5rem 1rem' }}
            >
              📎 Open PDF
            </a>
          </div>
        </div>
      );

    case 'video':
      return (
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: 'var(--border-radius)',
        }}>
          <video
            controls
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              borderRadius: 'var(--border-radius)',
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          >
            <source src={file.url} type={file.file_type} />
            Your browser does not support the video tag.
          </video>
          <div style={{
            display: 'none',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            color: 'var(--text-secondary)',
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>
              🎥
            </div>
            <p>Video preview not available</p>
            <a
              href={file.url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
              style={{ marginTop: '1rem', padding: '0.5rem 1rem' }}
            >
              📎 Open Video
            </a>
          </div>
        </div>
      );

    case 'audio':
      return (
        <div style={{
          width: '100%',
          padding: '2rem',
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: 'var(--border-radius)',
        }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>
              🎵
            </div>
            <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--text-primary)' }}>
              {file.title || file.original_filename}
            </h3>
            <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
              {file.file_type || 'Audio file'}
            </p>
          </div>
          <audio
            controls
            style={{
              width: '100%',
              maxWidth: '400px',
              margin: '0 auto',
              display: 'block',
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          >
            <source src={file.url} type={file.file_type} />
            Your browser does not support the audio tag.
          </audio>
          <div style={{
            display: 'none',
            textAlign: 'center',
            marginTop: '2rem',
          }}>
            <a
              href={file.url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
              style={{ padding: '0.5rem 1rem' }}
            >
              📎 Download Audio
            </a>
          </div>
        </div>
      );

    case 'text':
      return (
        <div style={{
          width: '100%',
          height: '100%',
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: 'var(--border-radius)',
          padding: '1rem',
        }}>
          <div style={{ marginBottom: '1rem' }}>
            <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--text-primary)' }}>
              {file.title || file.original_filename}
            </h3>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              {file.file_type || 'Text file'}
            </p>
          </div>
          <div style={{
            backgroundColor: 'var(--bg-primary)',
            border: '1px solid var(--border-color)',
            borderRadius: 'var(--border-radius)',
            padding: '1rem',
            height: 'calc(100% - 80px)',
            overflow: 'auto',
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            color: 'var(--text-primary)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}>
            Loading file content...
            {/* Note: In a real implementation, you would fetch the file content here */}
            <div style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
              Text file preview not implemented in demo.
              <br /><br />
              <a
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
                style={{ marginTop: '1rem', padding: '0.5rem 1rem', display: 'inline-block' }}
              >
                📎 Open File
              </a>
            </div>
          </div>
        </div>
      );

    default:
      return (
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: 'var(--border-radius)',
        }}>
          <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>
              📄
            </div>
            <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--text-primary)' }}>
              {file.title || file.original_filename}
            </h3>
            <p style={{ margin: '0 0 1.5rem 0', fontSize: '0.9rem' }}>
              {file.file_type || 'Unknown file type'}
            </p>
            <a
              href={file.url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
              style={{ padding: '0.75rem 1.5rem' }}
            >
              📎 Open/Download File
            </a>
          </div>
        </div>
      );
  }
};

const FileViewModal = ({ file, isOpen, onClose, onStatusUpdate }) => {
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(file?.status || 'pending');
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
  });
  const [replyMessage, setReplyMessage] = useState('');
  const [replyFile, setReplyFile] = useState(null);
  const [replySending, setReplySending] = useState(false);
  const [replySuccess, setReplySuccess] = useState('');
  const [replyError, setReplyError] = useState('');

  if (!isOpen || !file) return null;

  const validStatuses = [
    { value: 'pending', label: 'Pending', color: '#ffc107' },
    { value: 'process', label: 'Processing', color: '#17a2b8' },
    { value: 'approved', label: 'Approved', color: '#28a745' },
    { value: 'reject', label: 'Rejected', color: '#dc3545' },
  ];

  // Check if status update is allowed
  const canUpdateStatus = file?.status === 'pending' || file?.status === 'process';

  const handleStatusChange = async (newStatus) => {
    // Show confirmation dialog
    const currentStatusText = validStatuses.find(s => s.value === file.status)?.label || 'Unknown';
    const newStatusText = validStatuses.find(s => s.value === newStatus)?.label || 'Unknown';

    setConfirmationModal({
      isOpen: true,
      title: 'Confirm Status Change',
      message: `Are you sure you want to change the status from "${currentStatusText}" to "${newStatusText}"?\n\nThis action cannot be undone.`,
      onConfirm: async () => {
        setUpdatingStatus(true);
        try {
          const result = await onStatusUpdate(file._id, newStatus);
          if (result.success) {
            setSelectedStatus(newStatus);
          }
        } catch (error) {
          console.error('Status update failed:', error);
        } finally {
          setUpdatingStatus(false);
        }
      },
    });
  };

  const handleCloseConfirmationModal = () => {
    setConfirmationModal({
      isOpen: false,
      title: '',
      message: '',
      onConfirm: null,
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: window.innerWidth <= 768 ? '1rem' : '2rem',
    }}>
      <div style={{
        backgroundColor: 'var(--bg-card)',
        borderRadius: 'var(--border-radius)',
        border: '1px solid var(--border-color)',
        width: '100%',
        maxWidth: window.innerWidth <= 768 ? '95vw' : '1200px',
        maxHeight: window.innerWidth <= 768 ? '95vh' : '90vh',
        overflow: 'hidden',
        boxShadow: 'var(--shadow-lg)',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Modal Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid var(--border-color)',
          backgroundColor: 'var(--bg-secondary)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <img
              src="https://taxntips.com/nav-logo.png"
              alt="TaxnTip Logo"
              style={{
                height: '30px',
                width: 'auto',
                marginRight: '1rem',
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>
              Document Details
            </h3>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              padding: '0.25rem',
            }}
          >
            ×
          </button>
        </div>

        {/* Modal Body - Two Column Layout */}
        <div style={{
          flex: 1,
          display: 'flex',
          overflow: 'hidden',
          flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
        }}>
          {/* Left Column - File Content (Scrollable) */}
          <div style={{
            flex: window.innerWidth <= 768 ? 'none' : 1,
            height: window.innerWidth <= 768 ? '300px' : 'auto',
            borderRight: window.innerWidth <= 768 ? 'none' : '1px solid var(--border-color)',
            borderBottom: window.innerWidth <= 768 ? '1px solid var(--border-color)' : 'none',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'var(--bg-secondary)',
          }}>
            <div style={{
              padding: '1rem 1.5rem',
              borderBottom: '1px solid var(--border-color)',
              backgroundColor: 'var(--bg-tertiary)',
            }}>
              <h4 style={{ margin: 0, color: 'var(--text-primary)' }}>
                📄 File Preview
              </h4>
            </div>

            <div style={{
              flex: 1,
              padding: '1.5rem',
              overflow: 'auto',
              backgroundColor: 'var(--bg-primary)',
            }}>
              {file.url ? (
                <FilePreview file={file} />
              ) : (
                <div style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--text-muted)',
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>
                      🚫
                    </div>
                    <p>File not available</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - File Information */}
          <div style={{
            width: window.innerWidth <= 768 ? '100%' : '400px',
            height: window.innerWidth <= 768 ? 'auto' : 'auto',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
            <div style={{
              flex: 1,
              overflow: 'auto',
              padding: '1.5rem',
            }}>
              {/* Status Management - Moved to Top */}
              <div style={{ marginBottom: '2rem' }}>
                <h4 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>
                  Status Management
                </h4>

                {!canUpdateStatus && (
                  <div style={{
                    padding: '1rem',
                    backgroundColor: 'rgba(255, 193, 7, 0.1)',
                    border: '1px solid rgba(255, 193, 7, 0.3)',
                    borderRadius: 'var(--border-radius)',
                    marginBottom: '1rem',
                    fontSize: '0.875rem',
                    color: '#ffc107',
                  }}>
                    <strong>Status Update Restricted:</strong><br />
                    Status can only be updated when the current status is "Pending" or "Processing".
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Current Status</label>
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexDirection: 'column' }}>
                    <select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      disabled={updatingStatus || !canUpdateStatus}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid var(--border-color)',
                        borderRadius: 'var(--border-radius)',
                        backgroundColor: canUpdateStatus ? 'var(--bg-secondary)' : 'var(--bg-tertiary)',
                        color: canUpdateStatus ? 'var(--text-primary)' : 'var(--text-muted)',
                        opacity: canUpdateStatus ? 1 : 0.6,
                      }}
                    >
                      {validStatuses.map(status => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>

                    <button
                      onClick={() => handleStatusChange(selectedStatus)}
                      disabled={updatingStatus || selectedStatus === file.status || !canUpdateStatus}
                      className="btn btn-primary"
                      style={{
                        width: '100%',
                        padding: '0.75rem 1.5rem',
                        opacity: canUpdateStatus ? 1 : 0.6,
                      }}
                    >
                      {updatingStatus ? (
                        <>
                          <div className="spinner" style={{ width: '16px', height: '16px', marginRight: '0.5rem' }}></div>
                          Updating...
                        </>
                      ) : (
                        'Update Status'
                      )}
                    </button>
                  </div>

                  {/* Current Status Badge */}
                  <div style={{
                    marginTop: '1rem',
                    display: 'inline-block',
                    padding: '0.25rem 0.5rem',
                    borderRadius: 'var(--border-radius-sm)',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    backgroundColor: validStatuses.find(s => s.value === file.status)?.color + '20',
                    color: validStatuses.find(s => s.value === file.status)?.color,
                    border: `1px solid ${validStatuses.find(s => s.value === file.status)?.color}40`,
                  }}>
                    Current: {validStatuses.find(s => s.value === file.status)?.label || 'Unknown'}
                  </div>
                </div>
              </div>

              {/* File Information */}
              <div style={{ marginBottom: '2rem' }}>
                <h4 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>
                  File Information
                </h4>

                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div className="form-group">
                    <label className="form-label">Title</label>
                    <div style={{
                      padding: '0.75rem',
                      backgroundColor: 'var(--bg-secondary)',
                      borderRadius: 'var(--border-radius)',
                      border: '1px solid var(--border-color)',
                      color: 'var(--text-primary)',
                    }}>
                      {file.title || file.original_filename}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <div style={{
                      padding: '0.75rem',
                      backgroundColor: 'var(--bg-secondary)',
                      borderRadius: 'var(--border-radius)',
                      border: '1px solid var(--border-color)',
                      color: 'var(--text-primary)',
                      minHeight: '80px',
                    }}>
                      {file.description || 'No description provided'}
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label className="form-label">File Size</label>
                      <div style={{
                        padding: '0.75rem',
                        backgroundColor: 'var(--bg-secondary)',
                        borderRadius: 'var(--border-radius)',
                        border: '1px solid var(--border-color)',
                        color: 'var(--text-primary)',
                      }}>
                        {formatFileSize(file.file_size)}
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">File Type</label>
                      <div style={{
                        padding: '0.75rem',
                        backgroundColor: 'var(--bg-secondary)',
                        borderRadius: 'var(--border-radius)',
                        border: '1px solid var(--border-color)',
                        color: 'var(--text-primary)',
                      }}>
                        {file.file_type || 'Unknown'}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label className="form-label">Folder</label>
                      <div style={{
                        padding: '0.75rem',
                        backgroundColor: 'var(--bg-secondary)',
                        borderRadius: 'var(--border-radius)',
                        border: '1px solid var(--border-color)',
                        color: 'var(--text-primary)',
                      }}>
                        {file.folder_name || 'N/A'}
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Upload Date</label>
                      <div style={{
                        padding: '0.75rem',
                        backgroundColor: 'var(--bg-secondary)',
                        borderRadius: 'var(--border-radius)',
                        border: '1px solid var(--border-color)',
                        color: 'var(--text-primary)',
                      }}>
                        {formatDate(file.created_date)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* User Information */}
              <div style={{ marginBottom: '2rem' }}>
                <h4 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>
                  Submitted By
                </h4>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label className="form-label">Name</label>
                    <div style={{
                      padding: '0.75rem',
                      backgroundColor: 'var(--bg-secondary)',
                      borderRadius: 'var(--border-radius)',
                      border: '1px solid var(--border-color)',
                      color: 'var(--text-primary)',
                    }}>
                      {file.user_id?.name || 'Unknown'}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <div style={{
                      padding: '0.75rem',
                      backgroundColor: 'var(--bg-secondary)',
                      borderRadius: 'var(--border-radius)',
                      border: '1px solid var(--border-color)',
                      color: 'var(--text-primary)',
                    }}>
                      {file.user_id?.email || 'Unknown'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Admin Reply */}
              <div style={{ marginBottom: '2rem' }}>
                <h4 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>
                  Admin Reply
                </h4>

                {replyError && (
                  <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
                    {replyError}
                  </div>
                )}
                {replySuccess && (
                  <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
                    {replySuccess}
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Message</label>
                  <textarea
                    className="form-input form-textarea"
                    placeholder="Write your message to the user..."
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Attach File (optional)</label>
                  <input
                    type="file"
                    className="form-input"
                    onChange={(e) => setReplyFile(e.target.files?.[0] || null)}
                  />
                </div>

                <div className="d-flex" style={{ gap: '0.5rem' }}>
                  <button
                    className="btn btn-primary"
                    disabled={replySending}
                    onClick={async () => {
                      setReplyError('');
                      setReplySuccess('');
                      if (!replyMessage.trim() && !replyFile) {
                        setReplyError('Please enter a message or attach a file.');
                        return;
                      }

                      try {
                        setReplySending(true);
                        const formData = new FormData();
                        formData.append('message', replyMessage.trim());
                        formData.append('fileId', file._id);
                        if (replyFile) {
                          formData.append('file', replyFile);
                        }

                        const res = await adminFileAPI.addAdminResponse(formData);
                        if (res.data?.success) {
                          setReplySuccess('Reply sent successfully.');
                          setReplyMessage('');
                          setReplyFile(null);
                        } else {
                          setReplyError(res.data?.message || 'Failed to send reply.');
                        }
                      } catch (err) {
                        setReplyError(err?.response?.data?.message || 'Failed to send reply.');
                      } finally {
                        setReplySending(false);
                      }
                    }}
                  >
                    {replySending ? (
                      <>
                        <div className="spinner" style={{ width: '16px', height: '16px' }}></div>
                        Sending...
                      </>
                    ) : (
                      'Send Reply'
                    )}
                  </button>
                  <button
                    className="btn btn-secondary"
                    disabled={replySending}
                    onClick={() => {
                      setReplyMessage('');
                      setReplyFile(null);
                      setReplyError('');
                      setReplySuccess('');
                    }}
                  >
                    Clear
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
      />
    </div>
  );
};

export default FileViewModal;
